// Multi-voice assignment panel for the project page.
// Lets the user assign a (provider, voice) per role/role_label found in the IR.

import { useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  voices as voicesApi,
  voiceAssignments,
  TTS_PROVIDERS,
  type TtsProvider,
  type VoiceAssignment,
  type VoiceDescriptor,
} from '../api.js';
import type { Ir } from '@scriptvoice/ir';

interface RoleSlot {
  role: string;
  role_label: string | null;
  sections: number;
}

function collectRoles(ir: Ir | null): RoleSlot[] {
  if (!ir) return [];
  const seen = new Map<string, RoleSlot>();
  for (const sec of ir.sections) {
    const role = sec.role ?? 'narrator';
    const label = sec.role_label ?? null;
    const key = `${role}::${label ?? ''}`;
    const slot = seen.get(key);
    if (slot) slot.sections += 1;
    else seen.set(key, { role, role_label: label, sections: 1 });
  }
  return Array.from(seen.values()).sort((a, b) => {
    if (a.role !== b.role) return a.role.localeCompare(b.role);
    return (a.role_label ?? '').localeCompare(b.role_label ?? '');
  });
}

interface Props {
  projectId: string;
  ir: Ir | null;
}

export function MultiVoicePanel({ projectId, ir }: Props) {
  const roles = useMemo(() => collectRoles(ir), [ir]);
  const [assignments, setAssignments] = useState<VoiceAssignment[]>([]);
  const [multiVoice, setMultiVoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await voiceAssignments.list(projectId);
      setAssignments(r.assignments);
      setMultiVoice(r.multi_voice);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
    }
  }

  useEffect(() => {
    void refresh();
  }, [projectId]);

  function assignmentFor(slot: RoleSlot): VoiceAssignment | undefined {
    return assignments.find(
      (a) => a.role === slot.role && (a.role_label ?? null) === (slot.role_label ?? null),
    );
  }

  async function handleClearAll() {
    if (!confirm('Remove all per-role voices? The render will fall back to a single voice.')) return;
    try {
      await voiceAssignments.clearAll(projectId);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Clear failed.');
    }
  }

  async function handleRemove(assignmentId: string) {
    try {
      await voiceAssignments.remove(projectId, assignmentId);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Remove failed.');
    }
  }

  if (!ir) {
    return (
      <div className="text-xs text-muted/70 text-center py-3">
        Direct the script first to see roles.
      </div>
    );
  }
  if (roles.length === 0) {
    return (
      <div className="text-xs text-muted/70 text-center py-3">
        No roles in this IR.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="text-xs text-muted">
          {multiVoice
            ? `${assignments.length}/${roles.length} roles have a custom voice`
            : 'Single-voice mode — assign per role to enable'}
        </div>
        {assignments.length > 0 && (
          <button
            onClick={handleClearAll}
            className="text-[10px] text-muted hover:text-accent"
          >
            clear all
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-accent bg-accent/5 border border-accent/20 rounded px-2 py-1">
          {error}
        </div>
      )}

      <ul className="space-y-2">
        {roles.map((slot) => {
          const key = `${slot.role}::${slot.role_label ?? ''}`;
          const assigned = assignmentFor(slot);
          const isEditing = editingKey === key;
          return (
            <li key={key} className="border border-soft rounded-md bg-white px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm">
                    {slot.role}
                    {slot.role_label && (
                      <span className="text-muted"> · {slot.role_label}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted/70">{slot.sections} sections</div>
                </div>
                {assigned ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono text-muted">
                      {assigned.provider}/{assigned.voice_id.slice(0, 16)}
                    </span>
                    <button
                      onClick={() => setEditingKey(isEditing ? null : key)}
                      className="text-[11px] text-muted hover:text-ink"
                    >
                      change
                    </button>
                    <button
                      onClick={() => handleRemove(assigned.id)}
                      className="text-[11px] text-muted hover:text-accent"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingKey(isEditing ? null : key)}
                    className="text-[11px] text-muted hover:text-ink shrink-0"
                  >
                    assign
                  </button>
                )}
              </div>

              {isEditing && (
                <AssignmentEditor
                  projectId={projectId}
                  slot={slot}
                  current={assigned}
                  onSaved={async () => {
                    setEditingKey(null);
                    await refresh();
                  }}
                  onCancel={() => setEditingKey(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AssignmentEditor({
  projectId,
  slot,
  current,
  onSaved,
  onCancel,
}: {
  projectId: string;
  slot: RoleSlot;
  current?: VoiceAssignment;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [provider, setProvider] = useState<TtsProvider>((current?.provider as TtsProvider) ?? 'openai');
  const [voiceId, setVoiceId] = useState<string>(current?.voice_id ?? '');
  const [voiceList, setVoiceList] = useState<VoiceDescriptor[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setVoiceList(null);
    voicesApi
      .list(provider)
      .then((r) => setVoiceList(r.voices))
      .catch(() => setVoiceList([]));
  }, [provider]);

  async function handleSave() {
    if (!voiceId) return;
    setSaving(true);
    setError(null);
    try {
      const selected = voiceList?.find((v) => v.id === voiceId);
      await voiceAssignments.upsert(projectId, {
        role: slot.role,
        role_label: slot.role_label,
        provider,
        voice_id: voiceId,
        voice_name: selected?.display_name ?? null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-soft/60 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <select
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value as TtsProvider);
            setVoiceId('');
          }}
          className="text-xs px-2 py-1 bg-parchment/60 border border-soft rounded"
        >
          {TTS_PROVIDERS.filter((p) => p.id !== 'playht').map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <select
          value={voiceId}
          onChange={(e) => setVoiceId(e.target.value)}
          className="text-xs px-2 py-1 bg-parchment/60 border border-soft rounded font-mono"
        >
          <option value="">— pick a voice —</option>
          {(voiceList ?? []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.display_name} · {v.gender}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <div className="text-[11px] text-accent">{error}</div>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={!voiceId || saving}
          className="text-xs bg-accent text-parchment px-3 py-1 rounded disabled:opacity-40"
        >
          {saving ? 'saving…' : 'save'}
        </button>
        <button onClick={onCancel} className="text-xs text-muted hover:text-ink px-2 py-1">
          cancel
        </button>
      </div>
    </div>
  );
}
